import type { ReactNode } from "react";

/**
 * The dashboard's visual vocabulary (docs/dashboard-ux-proposal.md).
 *
 * One fixed glyph set, reused everywhere. **Shape carries the meaning**, so
 * colour is never the only signal (accessibility), and a legend can appear once
 * instead of as repeated labels. Colour is fixed and semantic — never
 * decorative.
 *
 * These atoms are the foundation Phases 1–4 build on: the shield leads every run
 * surface, the ring shows coverage, the glyphs replace prose status badges.
 */

export type Tone = "success" | "warning" | "error" | "neutral" | "info" | "muted";

const TONE_CLASS: Record<Tone, string> = {
  success: "text-success",
  warning: "text-warning",
  error: "text-error",
  neutral: "text-neutral",
  info: "text-info",
  muted: "text-base-content/40",
};

export type GlyphSize = "xs" | "sm" | "md" | "lg";

const SIZE_PX: Record<GlyphSize, number> = { xs: 12, sm: 16, md: 20, lg: 28 };

export type GlyphShape =
  // coverage status
  | "checkCircle"
  | "xCircle"
  | "dashCircle"
  | "dashedCircle"
  | "halfCircle"
  | "arc"
  // outcome
  | "check"
  | "x"
  | "bang"
  | "approx"
  | "skip"
  // go / no-go
  | "shieldCheck"
  | "shieldX"
  | "shieldBang"
  | "shieldDash"
  // liveness
  | "dot"
  | "clock"
  | "stop"
  // origin
  | "compass"
  | "person"
  // findings
  | "plus"
  | "wave"
  // product area
  | "app"
  | "chip"
  | "cloud";

export type GlyphSpec = { shape: GlyphShape; tone: Tone; label: string };

/** Coverage status → glyph (docs: covered/partial/uncovered/blocked/planned/generating). */
export const COVERAGE_GLYPH: Record<string, GlyphSpec> = {
  covered: { shape: "checkCircle", tone: "success", label: "Covered" },
  partial: { shape: "halfCircle", tone: "warning", label: "Partial" },
  uncovered: { shape: "xCircle", tone: "error", label: "Uncovered" },
  blocked: { shape: "dashCircle", tone: "neutral", label: "Blocked" },
  planned: { shape: "dashedCircle", tone: "muted", label: "Planned" },
  generating: { shape: "arc", tone: "info", label: "Generating" },
};

/** Result outcome → glyph (pass/fail/error/flake/skipped). */
export const OUTCOME_GLYPH: Record<string, GlyphSpec> = {
  pass: { shape: "check", tone: "success", label: "Pass" },
  fail: { shape: "x", tone: "error", label: "Fail" },
  error: { shape: "bang", tone: "error", label: "Error" },
  flake: { shape: "approx", tone: "warning", label: "Flake" },
  skipped: { shape: "skip", tone: "muted", label: "Skipped" },
};

/**
 * Go/no-go → the product's most important atom.
 *
 * The backend currently emits ``go`` and ``no_go``; ``go_with_gaps`` is the
 * proposal's third state (policy met, but gaps remain) and is accepted here so
 * the vocabulary is ready for it.
 */
export const VERDICT_GLYPH: Record<string, GlyphSpec> = {
  go: { shape: "shieldCheck", tone: "success", label: "GO" },
  go_with_gaps: { shape: "shieldBang", tone: "warning", label: "GO with gaps" },
  no_go: { shape: "shieldX", tone: "error", label: "NO-GO" },
};

const PENDING_VERDICT: GlyphSpec = { shape: "shieldDash", tone: "muted", label: "Verdict pending" };

export const LIVENESS_GLYPH: Record<string, GlyphSpec> = {
  live: { shape: "dot", tone: "success", label: "Live" },
  stale: { shape: "clock", tone: "warning", label: "Stale" },
  ended: { shape: "stop", tone: "neutral", label: "Ended" },
};

export const ORIGIN_GLYPH: Record<string, GlyphSpec> = {
  spec_anchored: { shape: "shieldCheck", tone: "info", label: "Spec-anchored" },
  exploratory: { shape: "compass", tone: "info", label: "Exploratory" },
  human_authored: { shape: "person", tone: "info", label: "Human-authored" },
};

export const FINDING_GLYPH: Record<string, GlyphSpec> = {
  bug: { shape: "bang", tone: "error", label: "Bug" },
  spec_gap: { shape: "plus", tone: "warning", label: "Spec gap" },
  noise: { shape: "wave", tone: "muted", label: "Noise" },
};

/** Product-area catalog → glyph. Keys are the qa-product-registry catalogs. */
export const PRODUCT_GLYPH: Record<string, GlyphSpec> = {
  APP: { shape: "app", tone: "neutral", label: "App" },
  ADMIN: { shape: "app", tone: "neutral", label: "Admin" },
  I2CHG: { shape: "cloud", tone: "neutral", label: "Cloud" },
  SERVER: { shape: "cloud", tone: "neutral", label: "Server" },
  V3: { shape: "chip", tone: "neutral", label: "Firmware" },
  VUE: { shape: "chip", tone: "neutral", label: "Firmware" },
  FIRMWARE_UPDATES: { shape: "chip", tone: "neutral", label: "Firmware updates" },
};

function shapePaths(shape: GlyphShape): ReactNode {
  switch (shape) {
    case "checkCircle":
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M8 12.5l2.6 2.6L16 9.4" />
        </>
      );
    case "xCircle":
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M9 9l6 6M15 9l-6 6" />
        </>
      );
    case "dashCircle":
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M8.5 12h7" />
        </>
      );
    case "dashedCircle":
      return <circle cx="12" cy="12" r="9" strokeDasharray="3 3" />;
    case "halfCircle":
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor" stroke="none" />
        </>
      );
    case "arc":
      return (
        <>
          <circle cx="12" cy="12" r="9" strokeOpacity={0.25} />
          <path d="M12 3a9 9 0 1 1-9 9" />
        </>
      );
    case "check":
      return <path d="M5 13l4.5 4.5L19 7" />;
    case "x":
      return <path d="M6 6l12 12M18 6L6 18" />;
    case "bang":
      return (
        <>
          <path d="M12 5.5v8" />
          <circle cx="12" cy="18" r="1.1" fill="currentColor" stroke="none" />
        </>
      );
    case "approx":
      return <path d="M5 10c2-2.2 4-2.2 6 0s4 2.2 6 0M5 15c2-2.2 4-2.2 6 0s4 2.2 6 0" />;
    case "skip":
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M6.8 17.2l10.4-10.4" />
        </>
      );
    case "shieldCheck":
      return (
        <>
          <path d="M12 3l7 2.8v5c0 4.4-3 7.5-7 9-4-1.5-7-4.6-7-9v-5z" />
          <path d="M9 12l2.2 2.2L15.5 9.8" />
        </>
      );
    case "shieldX":
      return (
        <>
          <path d="M12 3l7 2.8v5c0 4.4-3 7.5-7 9-4-1.5-7-4.6-7-9v-5z" />
          <path d="M9.5 10.5l5 5M14.5 10.5l-5 5" />
        </>
      );
    case "shieldBang":
      return (
        <>
          <path d="M12 3l7 2.8v5c0 4.4-3 7.5-7 9-4-1.5-7-4.6-7-9v-5z" />
          <path d="M12 9v4" />
          <circle cx="12" cy="15.6" r="1" fill="currentColor" stroke="none" />
        </>
      );
    case "shieldDash":
      return (
        <>
          <path d="M12 3l7 2.8v5c0 4.4-3 7.5-7 9-4-1.5-7-4.6-7-9v-5z" />
          <path d="M9 12.5h6" />
        </>
      );
    case "dot":
      return <circle cx="12" cy="12" r="4.5" fill="currentColor" stroke="none" />;
    case "clock":
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7.5V12l3 2" />
        </>
      );
    case "stop":
      return <rect x="7" y="7" width="10" height="10" rx="1.5" />;
    case "compass":
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M15.6 8.4l-2 5.2-5.2 2 2-5.2z" />
        </>
      );
    case "person":
      return (
        <>
          <circle cx="12" cy="8" r="3.2" />
          <path d="M5.5 19.5c0-3.3 2.9-5.5 6.5-5.5s6.5 2.2 6.5 5.5" />
        </>
      );
    case "plus":
      return <path d="M12 6v12M6 12h12" />;
    case "wave":
      return <path d="M3 12c2.5-4 5-4 7.5 0s5 4 7.5 0" />;
    case "app":
      return (
        <>
          <rect x="7" y="3" width="10" height="18" rx="2.5" />
          <path d="M10.5 18.4h3" />
        </>
      );
    case "chip":
      return (
        <>
          <rect x="7" y="7" width="10" height="10" rx="1.5" />
          <path d="M10 3v4M14 3v4M10 17v4M14 17v4M3 10h4M3 14h4M17 10h4M17 14h4" />
        </>
      );
    case "cloud":
      return <path d="M7 18h10a3.5 3.5 0 0 0 .4-7A5 5 0 0 0 7.6 9.5 3.8 3.8 0 0 0 7 18z" />;
  }
}

export function Glyph({
  shape,
  tone = "neutral",
  size = "md",
  title,
  className,
}: {
  shape: GlyphShape;
  tone?: Tone;
  size?: GlyphSize;
  title?: string;
  className?: string;
}) {
  const px = SIZE_PX[size];
  return (
    <svg
      viewBox="0 0 24 24"
      width={px}
      height={px}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`inline-block shrink-0 align-[-0.15em] ${TONE_CLASS[tone]} ${className ?? ""}`}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {title ? <title>{title}</title> : null}
      {shapePaths(shape)}
    </svg>
  );
}

function SpecGlyph({ spec, size, title }: { spec: GlyphSpec; size?: GlyphSize; title?: string }) {
  return <Glyph shape={spec.shape} tone={spec.tone} size={size} title={title ?? spec.label} />;
}

export function CoverageGlyph({
  status,
  size,
  title,
}: {
  status: string;
  size?: GlyphSize;
  title?: string;
}) {
  return <SpecGlyph spec={COVERAGE_GLYPH[status] ?? COVERAGE_GLYPH.planned} size={size} title={title} />;
}

export function OutcomeGlyph({
  outcome,
  size,
  title,
}: {
  outcome: string;
  size?: GlyphSize;
  title?: string;
}) {
  return (
    <SpecGlyph
      spec={OUTCOME_GLYPH[outcome] ?? { shape: "skip", tone: "muted", label: outcome }}
      size={size}
      title={title}
    />
  );
}

export function VerdictShield({
  verdict,
  size = "md",
  title,
}: {
  verdict: string | null;
  size?: GlyphSize;
  title?: string;
}) {
  const spec = verdict ? (VERDICT_GLYPH[verdict] ?? PENDING_VERDICT) : PENDING_VERDICT;
  return <SpecGlyph spec={spec} size={size} title={title ?? spec.label} />;
}

export function LivenessDot({
  liveness,
  size = "sm",
  title,
}: {
  liveness: string;
  size?: GlyphSize;
  title?: string;
}) {
  const spec = LIVENESS_GLYPH[liveness] ?? LIVENESS_GLYPH.ended;
  return (
    <Glyph
      shape={spec.shape}
      tone={spec.tone}
      size={size}
      title={title ?? spec.label}
      className={liveness === "live" ? "animate-pulse" : undefined}
    />
  );
}

export function OriginGlyph({ origin, size = "sm" }: { origin: string; size?: GlyphSize }) {
  const spec = ORIGIN_GLYPH[origin] ?? { shape: "person", tone: "muted" as Tone, label: origin };
  return <SpecGlyph spec={spec} size={size} />;
}

export function FindingGlyph({ finding, size = "sm" }: { finding: string; size?: GlyphSize }) {
  const spec = FINDING_GLYPH[finding] ?? { shape: "wave", tone: "muted" as Tone, label: finding };
  return <SpecGlyph spec={spec} size={size} />;
}

export function ProductIcon({ area, size = "sm" }: { area: string; size?: GlyphSize }) {
  const spec = PRODUCT_GLYPH[area] ?? { shape: "app", tone: "muted" as Tone, label: area };
  return <SpecGlyph spec={spec} size={size} />;
}

/**
 * Coverage as a ring: the "one atom" that summarises a run at a glance.
 * Tone is derived from the percentage against the usual 90/70 thresholds.
 */
export function CoverageRing({
  pct,
  size = 44,
  label,
}: {
  pct: number;
  size?: number;
  label?: string;
}) {
  const clamped = Math.max(0, Math.min(100, pct));
  const tone: Tone = clamped >= 90 ? "success" : clamped >= 70 ? "warning" : "error";
  const r = (size - 6) / 2;
  const circumference = 2 * Math.PI * r;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={TONE_CLASS[tone]}
      role="img"
      aria-label={label ?? `Coverage ${clamped.toFixed(0)} percent`}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="currentColor"
        strokeOpacity={0.2}
        strokeWidth={4}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="currentColor"
        strokeWidth={4}
        strokeLinecap="round"
        strokeDasharray={`${(circumference * clamped) / 100} ${circumference}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        fill="currentColor"
        fontSize={size * 0.3}
        fontWeight={600}
      >
        {Math.round(clamped)}
      </text>
    </svg>
  );
}

/** Risk weight as a five-segment bar (red high, amber medium, green low). */
export function RiskBar({ weight, segments = 5 }: { weight: number | null; segments?: number }) {
  const filled =
    weight == null ? 0 : Math.max(0, Math.min(segments, Math.round(weight * segments)));
  const tone: Tone =
    weight == null ? "muted" : weight >= 0.7 ? "error" : weight >= 0.4 ? "warning" : "success";
  return (
    <span
      className={`inline-flex items-center gap-[2px] ${TONE_CLASS[tone]}`}
      role="img"
      aria-label={weight == null ? "No risk weight" : `Risk ${weight.toFixed(2)} of 1`}
    >
      {Array.from({ length: segments }, (_, i) => (
        <span
          key={i}
          className={`h-3 w-[3px] rounded-sm bg-current ${i < filled ? "" : "opacity-20"}`}
        />
      ))}
    </span>
  );
}

/** Legend rows for the vocabulary, rendered once rather than as repeated labels. */
export const GLYPH_LEGEND: { heading: string; items: { spec: GlyphSpec; hint?: string }[] }[] = [
  {
    heading: "Coverage",
    items: Object.values(COVERAGE_GLYPH).map((spec) => ({ spec })),
  },
  {
    heading: "Outcome",
    items: Object.values(OUTCOME_GLYPH).map((spec) => ({ spec })),
  },
  {
    heading: "Go / No-Go",
    items: [
      VERDICT_GLYPH.go,
      VERDICT_GLYPH.go_with_gaps,
      VERDICT_GLYPH.no_go,
      PENDING_VERDICT,
    ].map((spec) => ({ spec })),
  },
  {
    heading: "Liveness",
    items: Object.values(LIVENESS_GLYPH).map((spec) => ({ spec })),
  },
];
